import config from '../../config';

import { Person, ClipSelection } from '../../types';
import { isEmpty, getFileImg } from '../../utils';

import {
  getAccessTokenClient,
  getApiKeyClient,
  getAttendeeClient,
} from '../client/http';

const {
  apiBaseUrl: { elements: baseUrl, media: mediaUrl },
} = config;

const accessTokenClient = () => getAccessTokenClient(baseUrl);
const mediaAccessTokenClient = () => getAccessTokenClient(mediaUrl);
const apiKeyClient = () => getApiKeyClient(baseUrl);
const mediaAttendeeClient = () => getAttendeeClient(mediaUrl);

export const generateCreations = async (
  eventId: string,
  clipSelections: ClipSelection[],
  isPublic?: boolean,
) => {
  try {
    const client = isPublic ? mediaAttendeeClient : mediaAccessTokenClient;
    await client().post('/api/generate-creations', { eventId, clipSelections });
  } catch (err: any) {
    if (err.response.data.message?.includes('Invalid Video Template'))
      throw new Error('Your template has some missing wildcards');
    else throw new Error('Error generating creations. Try again later');
  }
};

export const getCreationResults = async (
  eventId: string,
  isPublic?: boolean,
) => {
  try {
    const client = isPublic ? apiKeyClient : accessTokenClient;
    const response = await client().get(
      `api/creation-results?eventId=${eventId}`,
    );

    let generatedFaces: Person[] = [];

    await Promise.all(
      response.data.results.map(async (person: Person) => {
        if (isEmpty(person.creations)) return;

        person.img = getFileImg(person.images?.[0]);

        if (person.metrics)
          person.metrics = {
            ...person.metrics,
            emailedCount:
              (person.metrics.emailedCount ?? 0) +
              (person.face.attendeeReviewData?.emailed ? 1 : 0),
          };

        generatedFaces.push(person);
      }),
    );

    const sortedFaces = generatedFaces.sort((one, other) => {
      if (new Date(one.face.created) > new Date(other.face.created)) return 1;
      return -1;
    });

    return sortedFaces;
  } catch (err: any) {
    throw new Error(`Error getting creation results: ${err.message}`);
  }
};

// public API used in share video page
export const getOneCreation = async (parentElementId: string) => {
  try {
    const response = await apiKeyClient().get(
      `api/files/by-parent-element/${parentElementId}?fileKind=video-creation`,
    );
    return response.data.files[0];
  } catch (err: any) {
    throw new Error(`Error getting a creation: ${err.message}`);
  }
};

export const getCreationWithData = async (eventId: string, faceId: string) => {
  const people = await getCreationResults(eventId, true);

  const foundCPerson = people.find(
    (person) => person.face.elementId === faceId,
  );

  return foundCPerson;
};
